import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronRight, LogOut, Menu, Mountain, PanelLeftClose, PanelLeftOpen, UserCircle2, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { ProductionEditingGuardBanner } from '../ProductionEditingGuardBanner'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '../ui/dialog'
import { useAuth } from '../../hooks/useAuth'
import type { DashboardUserRole } from '../../lib/api/auth'
import { baseCampVersion } from '../../lib/appVersion'
import { cn } from '../../utils/cn'
import { getDashboardEnvironment, type DashboardEnvironment } from '../../utils/env'
import { DashboardCommandPalette } from './DashboardCommandPalette'
import { DashboardNav } from './DashboardNav'
import { getDashboardRouteContext } from './nav-items'
import { TrackingDiagnosticsAlert } from './TrackingDiagnosticsAlert'

const ENVIRONMENT_LABELS: Record<DashboardEnvironment, { label: string; className: string }> = {
  live: { label: 'LIVE', className: 'border-emerald-600/25 bg-emerald-50 text-emerald-800' },
  staging: { label: 'STAGING', className: 'border-amber-600/25 bg-amber-50 text-amber-800' },
  local: { label: 'LOCAL', className: 'border-slate-400/30 bg-slate-50 text-slate-600' },
  preview: { label: 'PREVIEW', className: 'border-violet-500/25 bg-violet-50 text-violet-800' }
}

const Brand = ({ collapsed = false }: { collapsed?: boolean }) => (
  <div className={cn('flex min-h-[88px] shrink-0 items-center gap-3 border-b border-white/10 px-5', collapsed && 'justify-center px-3')}>
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/[0.06] text-[#d0e8c5]">
      <Mountain aria-hidden="true" className="h-6 w-6" strokeWidth={1.6} />
    </span>
    {!collapsed && <div><p className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/60">HikeStrong</p><p className="text-xl font-semibold tracking-tight text-white">Base Camp</p></div>}
  </div>
)

export const DashboardLayout = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [isNavCollapsed, setIsNavCollapsed] = useState(false)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const accountButtonRef = useRef<HTMLButtonElement>(null)
  const environment = ENVIRONMENT_LABELS[getDashboardEnvironment()]
  const role = typeof user?.role === 'string' ? user.role as DashboardUserRole : undefined
  const context = getDashboardRouteContext(pathname, role)
  const userInitial = user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'A'

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) setIsUserMenuOpen(false)
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isUserMenuOpen) {
        setIsUserMenuOpen(false)
        accountButtonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isUserMenuOpen])

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)')
    const closeMobileNavigation = () => { if (desktop.matches) setIsMobileNavOpen(false) }
    desktop.addEventListener('change', closeMobileNavigation)
    return () => desktop.removeEventListener('change', closeMobileNavigation)
  }, [])

  const handleOpenProfile = () => { setIsUserMenuOpen(false); navigate('/profile') }
  const handleSignOut = () => { setIsUserMenuOpen(false); void logout(); navigate('/login', { replace: true }) }

  return (
    <Dialog open={isMobileNavOpen} onOpenChange={setIsMobileNavOpen}>
      <div className="flex h-dvh overflow-hidden bg-background">
        <a href="#workspace-content" className="basecamp-skip-link">Skip to page content</a>
        <aside className={cn('basecamp-sidebar hidden shrink-0 flex-col overflow-hidden lg:flex', isNavCollapsed ? 'w-[76px]' : 'w-[248px]')} aria-label="Sidebar">
          <Brand collapsed={isNavCollapsed} />
          <div id="desktop-navigation" className="min-h-0 flex-1 overflow-y-auto px-3 py-5"><DashboardNav isCollapsed={isNavCollapsed} /></div>
          <div className="flex min-h-16 items-center justify-between gap-2 border-t border-white/10 px-4 py-3">
            {!isNavCollapsed && <span className="font-mono text-[10px] text-white/60">v{baseCampVersion}</span>}
            <button type="button" onClick={() => setIsNavCollapsed((value) => !value)} aria-controls="desktop-navigation" aria-expanded={!isNavCollapsed} aria-label={isNavCollapsed ? 'Expand navigation' : 'Collapse navigation'} title={isNavCollapsed ? 'Expand navigation' : 'Collapse navigation'} className="flex h-9 w-9 items-center justify-center rounded-lg text-white/65 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70">
              {isNavCollapsed ? <PanelLeftOpen className="h-[18px] w-[18px]" /> : <PanelLeftClose className="h-[18px] w-[18px]" />}
            </button>
          </div>
        </aside>
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <header className="relative z-20 shrink-0 border-b border-border bg-card">
            <div className="flex h-[72px] items-center justify-between gap-3 px-4 sm:px-7 lg:px-8">
              <div className="flex min-w-0 items-center gap-3">
                <DialogTrigger asChild>
                  <button type="button" aria-label="Open navigation" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"><Menu className="h-[18px] w-[18px]" /></button>
                </DialogTrigger>
                <div aria-label="Current workspace" className="flex min-w-0 items-center gap-2 text-sm">
                  <span className="hidden shrink-0 text-muted-foreground sm:inline">{context?.groupLabel ?? 'Base Camp'}</span>
                  <ChevronRight aria-hidden="true" className="hidden h-3.5 w-3.5 shrink-0 text-muted-foreground/60 sm:block" />
                  <span className="truncate font-medium text-foreground">{context?.label ?? 'Base Camp'}</span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                <span aria-label={'Environment: ' + environment.label} className={cn('rounded-md border px-2 py-1 font-mono text-[10px] font-semibold tracking-[0.08em]', environment.className)}>{environment.label}</span>
                <DashboardCommandPalette />
                <div className="relative" ref={userMenuRef}>
                  <button ref={accountButtonRef} type="button" onClick={() => setIsUserMenuOpen((value) => !value)} aria-expanded={isUserMenuOpen} aria-controls="account-actions" aria-label={'Account for ' + (user?.displayName || user?.email || 'current user')} className="flex h-10 items-center gap-2 rounded-lg px-1.5 text-foreground hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full border border-primary/15 bg-primary/10 text-xs font-semibold text-primary">{userInitial}</span>
                    <span className="hidden max-w-36 truncate text-[13px] font-medium xl:inline">{user?.displayName || user?.email}</span>
                    <ChevronDown className={cn('hidden h-3.5 w-3.5 text-muted-foreground transition-transform sm:block', isUserMenuOpen && 'rotate-180')} />
                  </button>
                  {isUserMenuOpen && (
                    <div id="account-actions" className="absolute right-0 mt-2 w-64 overflow-hidden rounded-xl border border-border bg-card shadow-elevation-2">
                      <div className="border-b border-border px-4 py-3"><p className="text-sm font-medium text-foreground">{user?.displayName || 'Your account'}</p><p className="mt-0.5 truncate text-xs text-muted-foreground">{user?.email}</p></div>
                      <div className="space-y-1 p-1.5">
                        <button type="button" onClick={handleOpenProfile} className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-muted"><UserCircle2 className="h-4 w-4 text-muted-foreground" />Profile & settings</button>
                        <button type="button" onClick={handleSignOut} className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-red-600 hover:bg-red-50"><LogOut className="h-4 w-4" />Sign out</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {user?.role === 'Admin' && <TrackingDiagnosticsAlert />}
          </header>
          <div id="workspace-content" tabIndex={-1} className="basecamp-workspace min-h-0 flex-1 overflow-y-auto p-4 outline-none sm:p-7 lg:p-8">
            <ProductionEditingGuardBanner />
            <Outlet />
          </div>
        </main>
      </div>
      <DialogContent className="basecamp-sidebar basecamp-mobile-nav inset-y-0 left-0 flex h-dvh max-h-none w-[min(320px,88vw)] translate-x-0 flex-col overflow-hidden rounded-none border-0 p-0 text-white">
        <DialogTitle className="sr-only">Base Camp navigation</DialogTitle>
        <DialogDescription className="sr-only">Choose a workspace. Close navigation to return to the current page.</DialogDescription>
        <Brand />
        <DialogClose asChild><button type="button" aria-label="Close navigation" className="absolute right-3 top-6 flex h-10 w-10 items-center justify-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"><X className="h-5 w-5" /></button></DialogClose>
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-5"><DashboardNav onNavigate={() => setIsMobileNavOpen(false)} /></div>
        <div className="border-t border-white/10 px-6 py-4 font-mono text-[10px] text-white/60">Base Camp v{baseCampVersion}</div>
      </DialogContent>
    </Dialog>
  )
}
